import React from "react";
import { Link } from "react-router-dom";

const IMG_MAIN = "/about-julia.jpg";
const IMG_2 = "/about-card-beach.jpg";
const IMG_3 = "/about-watercolor.jpg";

export default function About() {
  return (
    <div data-testid="about-page" className="fade-up">
      {/* HERO */}
      <section className="max-w-[1120px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16 md:pb-24 text-center">
        <p className="overline hidden">About the Studio</p>
        <h1 className="font-serif italic font-light text-4xl sm:text-5xl md:text-6xl text-brand-dark mt-6 leading-[1.05]">
          Who We Is<br /> &amp; What We Is Do.
        </h1>
        <p className="mt-8 text-brand-neutral text-lg leading-relaxed max-w-2xl mx-auto">
          The Island Studio is an independent illustration and design studio based
          in Nassau, The Bahamas. Through greeting cards, original artwork,
          branding and bespoke creative projects, I create thoughtful work
          inspired by people, places and the everyday stories that connect us.
        </p>
      </section>

      {/* HI, I'M JULIA */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7 img-zoom aspect-[4/3] overflow-hidden">
            <img src={IMG_MAIN} alt="Julia, founder of The Island Studio, at work" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-5 md:pt-10 md:pl-8">
            <p className="overline hidden">The Founder</p>
            <h2 className="font-serif font-light text-2xl md:text-3xl mt-4 text-brand-dark leading-tight">
              Hi, I&apos;m <span className="italic">Julia</span>.
            </h2>
            <div className="mt-6 space-y-4 text-brand-neutral leading-relaxed">
              <p>I&apos;m a proud Bahamian and the founder of The Island Studio.</p>
              <p>
                My business is my love letter to my home, but it&apos;s also a love
                letter to the absolute joy I derive from good stationery.
              </p>
              <p>
                What began as a passion for illustration and greeting cards has
                grown into an independent design studio creating greeting cards,
                original artwork, branding and bespoke creative projects inspired
                by island life.
              </p>
              <p>
                My goal has always been simple: to create paper goods that
                Bahamians are proud to give and visitors are excited to take
                home. Along the way, I&apos;ve also discovered that it&apos;s the
                perfect excuse to express my humour and my slightly twisted mind
                in the only way I know how.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DESIGNED, DRAWN AND FINISHED ON-ISLAND */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5 md:pr-8 order-2 md:order-1">
            <p className="overline hidden">Made in The Bahamas</p>
            <h2 className="font-serif font-light text-2xl md:text-3xl mt-4 text-brand-dark leading-tight">
              Designed, Drawn and<br /> Finished On-Island.
            </h2>
            <div className="mt-6 space-y-4 text-brand-neutral leading-relaxed">
              <p>
                From the very first pencil sketch or digital mark to the sticker
                that seals the packaging, everything is created in Nassau. Every
                idea is developed, illustrated, designed, printed, assembled and
                packed right here in The Bahamas.
              </p>
              <p>
                Working this way is a deliberate choice. It allows me to stay
                closely connected to every stage of the process while supporting
                local industry and creating work that genuinely reflects the
                place it comes from.
              </p>
              <p>
                Every collection and every project is shaped by the everyday
                beauty, humour and character of these islands. It isn&apos;t
                simply designed in The Bahamas—it&apos;s made here from start to
                finish, and I&apos;m incredibly proud of that.
              </p>
            </div>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4 order-1 md:order-2">
            <div className="img-zoom aspect-[3/4] overflow-hidden">
              <img src={IMG_2} alt="Studio detail" className="w-full h-full object-cover" />
            </div>
            <div className="img-zoom aspect-[3/4] overflow-hidden mt-8">
              <img src={IMG_3} alt="Studio detail" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SHOP THE STUDIO */}
      <section className="bg-brand-surface paper-texture py-24 md:py-32">
        <div className="max-w-[1120px] mx-auto px-6 md:px-12 text-center">
          <p className="overline hidden">The Studio</p>
          <h2 className="font-serif font-light text-3xl md:text-5xl mt-4 text-brand-dark leading-tight">
            Know the <span className="italic">Studio</span>.
          </h2>
          <div className="mt-8 max-w-2xl mx-auto space-y-4 text-brand-neutral leading-relaxed">
            <p>
              What began with greeting cards has grown into a broader design
              studio creating original artwork, visual identities, stationery and
              bespoke creative projects.
            </p>
            <p>
              While many of the collections are inspired by life in The Bahamas,
              the approach has always remained the same: to create work that
              feels thoughtful, personal and made to last.
            </p>
            <p>
              Every project begins by listening, observing and understanding the
              story behind it. The illustration comes afterwards.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/products" data-testid="about-shop-cta" className="btn-primary">
              Shop the Studio
            </Link>
            <Link to="/quote" data-testid="about-commission-cta" className="btn-outline">
              Commission Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
